import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Button, Typography, Row, Col, Statistic, Alert, Space, Spin, App as AntApp, Flex,
} from 'antd';
import {
  TeamOutlined, SettingOutlined, FileTextOutlined, RocketOutlined,
  ArrowRightOutlined, AppstoreOutlined, ShopOutlined, DollarOutlined,
  ProfileOutlined, UploadOutlined, FundOutlined, RobotOutlined,
} from '@ant-design/icons';
import { API_URL } from '../config';
import { usePerms } from '../hooks/usePerms';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmtC = (v: number): string => {
  const n = v || 0;
  if (n >= 1_000_000) return 'R$ ' + (n / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  if (n >= 1_000)     return 'R$ ' + Math.round(n / 1_000).toLocaleString('pt-BR') + 'k';
  return 'R$ ' + Math.round(n).toLocaleString('pt-BR');
};

type ModuloChave = 'financeiro' | 'comercial' | 'operacional' | 'licitacoes';
// Onda F5 (2026-05-28): removidos fallbacks por Cognito groups.
// Mantem so abasPermitidas legacy (GET /meu-perfil → perfil_permissoes antigo).
// Quem nao tem matriz nova (pode) E nao tem aba mapeada NAO vê o módulo.
function temAcesso(modulo: ModuloChave, abasPermitidas: string[] | null): boolean {
  if (!abasPermitidas) return false;
  const finAbas = ['dashboard', 'upload', 'profissionais', 'diferimentos', 'manuais', 'orcamento', 'centros-custo', 'categorias', 'patrimonio', 'emprestimos'];
  const comAbas = ['com-pipeline', 'com-clientes', 'com-contatos', 'com-custos', 'com-composicoes', 'com-propostas', 'com-dashboard'];
  const opAbas  = ['op-contratos'];
  const licAbas = ['com-licitacoes'];
  if (modulo === 'financeiro')   return finAbas.some(a => abasPermitidas.includes(a));
  if (modulo === 'comercial')    return comAbas.some(a => abasPermitidas.includes(a));
  if (modulo === 'operacional')  return opAbas.some(a => abasPermitidas.includes(a));
  if (modulo === 'licitacoes')   return licAbas.some(a => abasPermitidas.includes(a));
  return false;
}

interface Props {
  groups: string[];  // legacy — nao usado mais, mas mantido pra nao quebrar caller
  abasPermitidas: string[] | null;
  onIrPara: (modulo: string, pagina: string) => void;
}

function HomeDashboardInner({ groups, abasPermitidas, onIrPara }: Props) {
  void groups;  // F5: ignorado, matriz nova substitui
  // Onda B2: matriz granular (admin.perfis). `pode` tem prioridade; fallback
  // pro temAcesso() antigo pra perfis que ainda nao tem perm code mapeada.
  // `carregandoPerms` evita o glitch "Sem acesso a módulos" enquanto fetch
  // do /meu_perfil_perms nao completou.
  const { pode, carregando: carregandoPerms } = usePerms();
  const verFin = pode('financeiro.dre.ver') || pode('financeiro.contas_receber.ver') || pode('financeiro.contas_pagar.ver') || temAcesso('financeiro', abasPermitidas);
  const verCom = pode('comercial.dashboard.ver') || pode('comercial.pipeline.ver') || temAcesso('comercial',  abasPermitidas);
  const verOp  = pode('operacional.projetos.listar') || pode('operacional.painel_geral.ver') || temAcesso('operacional', abasPermitidas);
  const verLic = pode('comercial.licitacoes.oportunidades.ver') || temAcesso('licitacoes', abasPermitidas);

  const [comData, setComData] = useState<any>(null);
  const [opData, setOpData]   = useState<{ contratos: any[]; abertas: number; atrasadas: number; concluidas30d: number } | null>(null);
  const [licData, setLicData] = useState<{ total: number; novas: number; em_analise: number } | null>(null);
  const [entregasData, setEntregasData] = useState<{
    mesCorrente: number; mesAnterior: number; ultimas12m: number;
    qtdMes: number; ultimasEntregas: any[];
  } | null>(null);

  useEffect(() => { if (verCom) carregarComercial(); }, [verCom]);
  useEffect(() => { if (verOp) { carregarOperacional(); carregarEntregas(); } }, [verOp]);
  useEffect(() => { if (verLic) carregarLicitacoes(); }, [verLic]);

  async function carregarEntregas() {
    try {
      const token = await getToken();
      const hoje = new Date();
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().slice(0, 10);
      const fimMes    = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().slice(0, 10);
      const inicioAnt = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1).toISOString().slice(0, 10);
      const fimAnt    = new Date(hoje.getFullYear(), hoje.getMonth(), 0).toISOString().slice(0, 10);
      const inicio12 = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1).toISOString().slice(0, 10);
      const post = (body: any) => axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_metrica_entregas', ...body }, { headers: { Authorization: token } });
      const [mes, ant, doze] = await Promise.all([
        post({ de: inicioMes, ate: fimMes }),
        post({ de: inicioAnt, ate: fimAnt }),
        post({ de: inicio12, ate: fimMes }),
      ]);
      const d = (r: any) => r.data.body ? JSON.parse(r.data.body) : r.data;
      const m = d(mes), a = d(ant), z = d(doze);
      setEntregasData({
        mesCorrente: m.total || 0, mesAnterior: a.total || 0, ultimas12m: z.total || 0,
        qtdMes: m.qtd || 0, ultimasEntregas: (z.entregas || []).slice(0, 5),
      });
    } catch {}
  }

  async function carregarComercial() {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/comercial/dashboard?janela=12m`, { headers: { Authorization: token } });
      setComData(res.data.body ? JSON.parse(res.data.body) : res.data);
    } catch {}
  }

  async function carregarOperacional() {
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'op_listar_contratos' },
        { headers: { Authorization: token } });
      const contratos = r.data.body ? JSON.parse(r.data.body) : r.data;
      const lista = Array.isArray(contratos) ? contratos : [];
      const ativos = lista.filter((c: any) =>
        ['planejamento', 'kickoff_pendente', 'em_execucao', 'aguardando_aprovacao'].includes(c.status));
      const hoje = new Date();
      const atrasadas = lista.filter((c: any) =>
        c.data_termino_previsto
        && new Date(c.data_termino_previsto) < hoje
        && c.status !== 'concluido' && c.status !== 'cancelado'
      ).length;
      const trintaAtras = new Date(); trintaAtras.setDate(trintaAtras.getDate() - 30);
      const concl30 = lista.filter((c: any) =>
        c.status === 'concluido' && c.data_termino_real
        && new Date(c.data_termino_real) >= trintaAtras
      ).length;
      setOpData({ contratos: ativos, abertas: ativos.length, atrasadas, concluidas30d: concl30 });
    } catch {}
  }

  async function carregarLicitacoes() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/comercial/licitacoes?limit=500`,
        { headers: { Authorization: token } });
      const data = r.data.body ? JSON.parse(r.data.body) : r.data;
      const lista = Array.isArray(data) ? data : (data.items || []);
      const novas = lista.filter((o: any) => o.status_interno === 'novo').length;
      const ema = lista.filter((o: any) => o.status_interno === 'em_analise').length;
      const ativas = lista.filter((o: any) => ['novo', 'em_analise', 'qualificada'].includes(o.status_interno)).length;
      setLicData({ total: ativas, novas, em_analise: ema });
    } catch {}
  }

  const hojeStr = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 22 }}>
        <Title level={2} style={{ margin: 0, color: '#020D7F' }}>Visão Geral</Title>
        <Text type="secondary" style={{ fontSize: 12, textTransform: 'capitalize' }}>{hojeStr}</Text>
      </div>

      {/* Enquanto perms carregam, nao mostrar aviso de "sem acesso" — evita
          glitch que aparecia brevemente entre login e fetch das perms. */}
      {!carregandoPerms && !verFin && !verCom && !verOp && !verLic && (
        <Alert
          type="warning"
          message="Sem acesso a módulos"
          description="Você ainda não tem acesso a nenhum módulo. Solicite ao administrador."
          showIcon
        />
      )}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {verCom && (
          <CardModulo
            titulo="Comercial — últimos 12 meses"
            icone={<TeamOutlined />}
            cor="#0930D0"
            loading={!comData}
            onAbrir={() => onIrPara('comercial', 'com-dashboard')}
          >
            {comData && (
              <Row gutter={10}>
                <Col xs={12} sm={6}>
                  <Statistic title="Receita ganha" value={fmtC(comData.receita.total)} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                  <Text type="secondary" style={{ fontSize: 10 }}>{comData.receita.n} negócio(s)</Text>
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="Pipeline ativo" value={fmtC(comData.pipeline_ativo.total)} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                  <Text type="secondary" style={{ fontSize: 10 }}>{comData.pipeline_ativo.n} aberto(s)</Text>
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="Ticket médio" value={fmtC(comData.receita.ticket_medio)} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                  <Text type="secondary" style={{ fontSize: 10 }}>por negócio</Text>
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic title="Win rate" value={comData.win_rate_pct} precision={1} suffix="%" valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                  <Text type="secondary" style={{ fontSize: 10 }}>{comData.ganhos}G · {comData.perdidos}P</Text>
                </Col>
              </Row>
            )}
          </CardModulo>
        )}

        {verOp && (
          <CardModulo
            titulo="Operacional — contratos em execução"
            icone={<SettingOutlined />}
            cor="#7C3AED"
            loading={!opData}
            onAbrir={() => onIrPara('operacional', 'op-contratos')}
          >
            {opData && (
              opData.abertas === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Nenhum contrato em execução. Inicie um a partir de um negócio com status "Ganho".
                </Text>
              ) : (
                <Row gutter={10}>
                  <Col xs={12} sm={8}>
                    <Statistic title="Em execução" value={opData.abertas} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {opData.abertas === 1 ? 'contrato ativo' : 'contratos ativos'}
                    </Text>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Statistic title="Atrasados" value={opData.atrasadas}
                      valueStyle={{ fontSize: 18, color: opData.atrasadas > 0 ? '#B91626' : '#020D7F' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>prazo previsto vencido</Text>
                  </Col>
                  <Col xs={12} sm={8}>
                    <Statistic title="Concluídos (30d)" value={opData.concluidas30d} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>encerrados no último mês</Text>
                  </Col>
                </Row>
              )
            )}
          </CardModulo>
        )}

        {verOp && (
          <CardModulo
            titulo="Entregas — produção mensal"
            icone={<RocketOutlined />}
            cor="#0a7f4a"
            loading={!entregasData}
            onAbrir={() => onIrPara('operacional', 'op-contratos')}
          >
            {entregasData && (
              entregasData.qtdMes === 0 && entregasData.ultimas12m === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Nenhuma entrega registrada ainda. Marque entregáveis como "✓ Entregar" no detalhe do contrato pra começar.
                </Text>
              ) : (
                <>
                  <Row gutter={10}>
                    <Col xs={12} sm={8}>
                      <Statistic title="Mês atual" value={fmtC(entregasData.mesCorrente)} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                      <Text type="secondary" style={{ fontSize: 10 }}>{entregasData.qtdMes} entrega(s)</Text>
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic title="Mês anterior" value={fmtC(entregasData.mesAnterior)} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        {entregasData.mesAnterior > 0
                          ? `${entregasData.mesCorrente >= entregasData.mesAnterior ? '+' : ''}${(((entregasData.mesCorrente - entregasData.mesAnterior) / entregasData.mesAnterior) * 100).toFixed(0)}% vs atual`
                          : 'sem comparativo'}
                      </Text>
                    </Col>
                    <Col xs={12} sm={8}>
                      <Statistic title="Últimos 12m" value={fmtC(entregasData.ultimas12m)} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                      <Text type="secondary" style={{ fontSize: 10 }}>acumulado</Text>
                    </Col>
                  </Row>
                  {entregasData.ultimasEntregas.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #F0F1F5' }}>
                      <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Últimas entregas
                      </Text>
                      {entregasData.ultimasEntregas.map((e: any) => (
                        <Flex key={e.id} gap={8} align="baseline" style={{ fontSize: 11, padding: '3px 0', color: '#555' }}>
                          <Text style={{ fontFamily: 'monospace', color: '#020D7F', minWidth: 80, fontSize: 11 }}>
                            {(e.contrato_numero || '').slice(0, 14)}
                          </Text>
                          <Text style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 }}>
                            {e.descricao}
                          </Text>
                          <Text strong style={{ color: '#0a7f4a', fontSize: 11 }}>{fmtC(e.valor || 0)}</Text>
                        </Flex>
                      ))}
                    </div>
                  )}
                </>
              )
            )}
          </CardModulo>
        )}

        {verLic && (
          <CardModulo
            titulo="Licitações — oportunidades públicas"
            icone={<FileTextOutlined />}
            cor="#B07500"
            loading={!licData}
            onAbrir={() => onIrPara('comercial', 'com-licitacoes')}
          >
            {licData && (
              licData.total === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>Sem oportunidades em análise no momento.</Text>
              ) : (
                <Row gutter={10}>
                  <Col xs={8}>
                    <Statistic title="Novas" value={licData.novas}
                      valueStyle={{ fontSize: 18, color: licData.novas > 0 ? '#B91626' : '#020D7F' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>aguardando triagem</Text>
                  </Col>
                  <Col xs={8}>
                    <Statistic title="Em análise" value={licData.em_analise} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>sob avaliação</Text>
                  </Col>
                  <Col xs={8}>
                    <Statistic title="Total ativas" value={licData.total} valueStyle={{ fontSize: 18, color: '#020D7F' }} />
                    <Text type="secondary" style={{ fontSize: 10 }}>acompanhamento</Text>
                  </Col>
                </Row>
              )
            )}
          </CardModulo>
        )}

        {verFin && (
          <CardModulo
            titulo="Financeiro — DRE"
            icone={<FundOutlined />}
            cor="#0a7f4a"
            loading={false}
            onAbrir={() => onIrPara('financeiro', 'dashboard')}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Acesse o módulo financeiro para ver o DRE detalhado, lançamentos e orçamento.
            </Text>
          </CardModulo>
        )}

        <CardModulo
          titulo="Suas atividades"
          icone={<ProfileOutlined />}
          cor="#0930D0"
          loading={false}
          onAbrir={() => onIrPara('atividades', 'atividades')}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            Atividades e tarefas atribuídas a você (cross-módulo: comercial, operacional, etc.)
          </Text>
        </CardModulo>
      </Space>

      <div style={{ marginTop: 22 }}>
        <Title level={5} style={{ color: '#020D7F', marginBottom: 10 }}>Atalhos rápidos</Title>
        <Row gutter={[10, 10]}>
          {verCom && (
            <>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<AppstoreOutlined />} titulo="Pipeline de negócios" onClick={() => onIrPara('comercial', 'com-pipeline')} /></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<FileTextOutlined />} titulo="Documentos comerciais" onClick={() => onIrPara('comercial', 'com-propostas')} /></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<ShopOutlined />} titulo="Empresas (clientes)" onClick={() => onIrPara('comercial', 'com-clientes')} /></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<DollarOutlined />} titulo="Composições de preço" onClick={() => onIrPara('comercial', 'com-composicoes')} /></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<RobotOutlined />} titulo="Conversar com Dwight" onClick={() => onIrPara('comercial', 'com-hydra')} /></Col>
            </>
          )}
          {verOp && (
            <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<SettingOutlined />} titulo="Contratos operacionais" onClick={() => onIrPara('operacional', 'op-contratos')} /></Col>
          )}
          {verLic && (
            <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<FileTextOutlined />} titulo="Licitações públicas" onClick={() => onIrPara('comercial', 'com-licitacoes')} /></Col>
          )}
          {verFin && (
            <>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<UploadOutlined />} titulo="Upload financeiro" onClick={() => onIrPara('financeiro', 'upload')} /></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<ProfileOutlined />} titulo="Lançamentos manuais" onClick={() => onIrPara('financeiro', 'manuais')} /></Col>
              <Col xs={24} sm={12} md={8} lg={6}><Atalho icone={<FundOutlined />} titulo="Orçamento anual" onClick={() => onIrPara('financeiro', 'orcamento')} /></Col>
            </>
          )}
        </Row>
      </div>
    </div>
  );
}

function CardModulo({ titulo, icone, cor, loading, children, onAbrir }: {
  titulo: string; icone: React.ReactNode; cor: string; loading: boolean; children: any; onAbrir: () => void;
}) {
  return (
    <Card
      size="small"
      style={{ borderTop: `3px solid ${cor}` }}
      title={
        <Flex align="center" gap={8}>
          <span style={{ fontSize: 16, color: cor }}>{icone}</span>
          <Text strong style={{ color: cor, fontSize: 14 }}>{titulo}</Text>
        </Flex>
      }
      extra={
        <Button size="small" type="default" style={{ borderColor: cor, color: cor }}
          onClick={onAbrir} icon={<ArrowRightOutlined />} iconPosition="end">
          Abrir módulo
        </Button>
      }
    >
      {loading ? (
        <Flex justify="center" align="center" style={{ minHeight: 60 }}>
          <Spin size="small" />
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>Carregando métricas...</Text>
        </Flex>
      ) : children}
    </Card>
  );
}

function Atalho({ icone, titulo, onClick }: { icone: React.ReactNode; titulo: string; onClick: () => void }) {
  return (
    <Button onClick={onClick} block
      style={{ height: 'auto', padding: '12px 14px', textAlign: 'left', fontSize: 13, fontWeight: 500 }}>
      <Flex gap={10} align="center">
        <span style={{ fontSize: 16, color: '#0930D0' }}>{icone}</span>
        {titulo}
      </Flex>
    </Button>
  );
}

export default function HomeDashboard(props: Props) {
  return (
    <AntApp>
      <HomeDashboardInner {...props} />
    </AntApp>
  );
}
